import "@/App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CandidateAuthProvider, useCandidateAuth } from "@/contexts/CandidateAuthContext";
import { ProtectedRoute } from "@/components/AuthShell";
import { RequireCapability } from "@/components/AccessRestricted";
import ErrorBoundary from "@/components/ErrorBoundary";
import BetaBanner from "@/components/BetaBanner";
import HelpBotMount, { HelpBotProvider } from "@/components/HelpBotMount";
import { bootstrapClarityMasks, syncClarityForRoute } from "@/lib/clarity";
import { initUtmSession } from "@/lib/utmSession";
import { hasA3ApplicationToken } from "@/lib/useApplyFlow";
import EmployerAgreementModal from "@/components/EmployerAgreementModal";
import AnalyticsProvider from "@/components/AnalyticsProvider";
// Batch 3.3 (2026-02-XX) — route-level code splitting for mobile perf.
// EAGERLY IMPORTED (stay in the initial bundle): landing + public + apply
// entry surfaces. This is the JS a candidate on /jobs/<slug> actually needs
// to render the page. Everything else (employer dashboard, ops console,
// interview/MCQ/AV/OCEAN flow, personality assessment) is React.lazy'd so
// the initial download for a landing candidate stays small.
//
// Rules of thumb for what belongs in EAGER vs LAZY:
//   EAGER  — anything a candidate hits from an ad click before submitting
//   LAZY   — deep app (protected employer/ops routes) + post-consent
//            candidate steps (MCQ / AV / OCEAN / Result / Journey / CV)
//
// PostHog / Clarity are UNCHANGED — they load from index.html on landing.
// Perf (2026-06): every page below was previously EAGER in main.js
// (~587 KiB of app src on every route). All converted to lazy — the
// single top-level <Suspense> already wraps <Routes>, and "/" keeps its
// static HTML hero shell while its chunk loads. No route/UX change.
const HomePage = lazy(() => import("@/pages/HomePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const VerifyOtpPage = lazy(() => import("@/pages/VerifyOtpPage"));
const RmOptOutPage = lazy(() => import("@/pages/RmOptOutPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));
const PublicJobsListPage = lazy(() => import("@/pages/PublicJobsListPage"));
const PublicJobDetailPage = lazy(() => import("@/pages/PublicJobDetailPage"));
const ApplyPage = lazy(() => import("@/pages/ApplyPage"));
const ApplyEntryPage = lazy(() => import("@/pages/ApplyEntryPage"));
const ApplyEntryVerifyPage = lazy(() => import("@/pages/ApplyEntryVerifyPage"));
const CandidateRegisterPage = lazy(() => import("@/pages/CandidateAuthPages").then((m) => ({ default: m.CandidateRegisterPage })));
const CandidateLoginPage = lazy(() => import("@/pages/CandidateAuthPages").then((m) => ({ default: m.CandidateLoginPage })));
const CandidateVerifyOtpPage = lazy(() => import("@/pages/CandidateAuthPages").then((m) => ({ default: m.CandidateVerifyOtpPage })));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ApplyThankYouPage = lazy(() => import("@/pages/ApplyThankYouPage"));

// LAZY-LOADED (out of the initial bundle) — every route below is behind
// an auth gate OR is deep in the candidate journey (post-consent). A
// landing candidate never downloads any of this JS unless they navigate
// into that route. Split by feature area so a single dashboard chunk
// doesn't drag in the ops console.

// Employer dashboard chunk
const DashboardPage           = lazy(() => import("@/pages/DashboardPage"));
const ActionQueueListPage     = lazy(() => import("@/pages/ActionQueueListPage"));
const ConsultantApp           = lazy(() => import("@/pages/ConsultantApp"));
const SlotsPage               = lazy(() => import("@/pages/SlotsPage"));
const JobEditPage             = lazy(() => import("@/pages/JobEditPage"));
// Batch 1 Social Boost (2026-02-11) — /go/:slug capture front-door.
const SocialBoostLandingPage  = lazy(() => import("@/pages/SocialBoostLandingPage"));
// Book-First social journey (2026-06) — /go/:slug/book-first second boost link.
const BookFirstLandingPage    = lazy(() => import("@/pages/BookFirstLandingPage"));
const ScreeningPreviewPage    = lazy(() => import("@/pages/ScreeningPreviewPage"));
const ScreeningBuilderPage    = lazy(() => import("@/pages/ScreeningBuilderPage"));
const JobPipelinePage         = lazy(() => import("@/pages/JobPipelinePage"));
const JobCampaignsPage        = lazy(() => import("@/pages/JobCampaignsPage"));
const JobFunnelPage           = lazy(() => import("@/pages/JobFunnelPage"));
const ApplicationReportPage   = lazy(() => import("@/pages/ApplicationReportPage"));
const JobSeedPage             = lazy(() => import("@/pages/JobSeedPage"));
const CompleteProfilePage     = lazy(() => import("@/pages/CompleteProfilePage"));
const PlansPage               = lazy(() => import("@/pages/PlansPage"));
const BillingPage             = lazy(() => import("@/pages/BillingPage"));
const BillingV3Page           = lazy(() => import("@/pages/BillingV3Page"));
const UsagePage               = lazy(() => import("@/pages/UsagePage"));
const CompanyProfilePage      = lazy(() => import("@/pages/CompanyProfilePage"));
const TeamMembersPage         = lazy(() => import("@/pages/TeamMembersPage"));
const AccountSettingsPage     = lazy(() => import("@/pages/AccountSettingsPage"));
const AcceptInvitePage        = lazy(() => import("@/pages/AcceptInvitePage"));

// Ops console chunk (Super admin — never loaded by a candidate)
const BmiOpsPage              = lazy(() => import("@/pages/BmiOpsPage"));
const BillingV3OpsPanel       = lazy(() => import("@/pages/bmi-ops/BillingV3OpsPanel"));
const JourneyLivePage         = lazy(() => import("@/pages/JourneyLivePage"));
const VerifyMobilePage        = lazy(() => import("@/pages/VerifyMobilePage"));

// Interviewer chunk
const InterviewerHomePage     = lazy(() => import("@/pages/InterviewerHomePage"));
const InterviewerBriefPage    = lazy(() => import("@/pages/InterviewerBriefPage"));
const ScorecardPage           = lazy(() => import("@/pages/ScorecardPage"));

// Deep candidate journey — post-consent, so never loaded on the /jobs/<slug>
// landing. UNTOUCHED behaviourally, just moved into a separate chunk.
const McqTakePage             = lazy(() => import("@/pages/McqTakePage"));
const AvTaskPage              = lazy(() => import("@/pages/AvTaskPage"));
const CvStepPage              = lazy(() => import("@/pages/CvStepPage"));
const OceanStepPage           = lazy(() => import("@/pages/OceanStepPage"));
const OceanTargetPage         = lazy(() => import("@/pages/OceanTargetPage"));
const ResultPage              = lazy(() => import("@/pages/ResultPage"));
const JourneyIntroPage        = lazy(() => import("@/pages/JourneyIntroPage"));
const CandidateDashboardPage  = lazy(() => import("@/pages/CandidateDashboardPage"));
const CandidateProfilePage    = lazy(() => import("@/pages/CandidateProfilePage"));
const PersonalityAssessmentPage = lazy(() => import("@/pages/PersonalityAssessmentPage"));
const PersonalityReportPage   = lazy(() => import("@/pages/PersonalityReportPage"));

// Lightweight fallback while a lazy chunk is downloading. Deliberately
// minimal — a spinner keeps CLS quiet (no arriving text pushing content
// around) and matches the app's existing loading states.
function RouteFallback() {
    return (
        <div className="min-h-[40vh] grid place-items-center text-muted-foreground text-sm">
            <div className="animate-pulse">Loading…</div>
        </div>
    );
}

function CandidateProtectedRoute({ children }) {
    const { candidate, loading } = useCandidateAuth();
    if (loading || candidate === null) return null;
    if (!candidate) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        return <Navigate to={`/candidate/login?next=${next}`} replace />;
    }
    return children;
}

// Batch A15 STOP-GATE rollback (2026-03-02) — the A3 post-OTP path
// is broken on prod (candidates bounce to homepage after verifying
// email), blocking every new candidate. Anonymous fallback here now
// sends candidates to `/candidate/register?next=…` (pre-A3 known-
// working flow). The A3 entry pages (`/apply/:slug/entry` +
// `.../verify`) are still mounted below for candidates who deep-link
// into them directly, but no code path routes NEW anonymous
// candidates there until the OTP-verify → CV loop is fixed.
function CandidateApplyProtectedRoute({ children }) {
    const { candidate, loading } = useCandidateAuth();
    // Reliability Hardening Batch 2 (2026-02-26) — QA-only bypass so
    // reserve-queue / retry copy can be reviewed via `?_demoFail=1`,
    // `?_demoGenerateError=1`, or `?_demoSubmitError=1` without needing
    // a real MCQ pass or forced API failure.
    if (typeof window !== "undefined") {
        try {
            const q = new URLSearchParams(window.location.search);
            if (q.get("_demoFail") || q.get("_demoGenerateError") || q.get("_demoSubmitError")) return children;
        } catch { /* noop */ }
    }
    if (loading || candidate === null) return null;
    if (!candidate) {
        // Batch 3.4 (2026-02-XX) — A3 kill-switch fix.
        //
        // ROOT CAUSE OF THE PRIOR A3 REGRESSION: this branch fired for
        // a just-verified A3 candidate (no full candidate session, only
        // sessionStorage tokens) and bounced them to /candidate/register.
        // The user completed CV upload → CvStepPage.navigate(`/jobs/${slug}/mcq`)
        // → hit THIS gate → redirect → loop back to homepage.
        //
        // The FIX: if the browser holds a valid A3 application-scoped
        // token in sessionStorage, treat this as an authenticated A3
        // candidate. Downstream pages (McqTakePage, ResultPage) already
        // authenticate their API calls via the token, so the auth model
        // is application-scoped, not candidate-account-scoped.
        //
        // This does NOT weaken security — the token is a signed
        // application-scoped credential granted only after email OTP
        // verification.
        if (hasA3ApplicationToken()) {
            return children;
        }
        // Rollback target: pre-A3 legacy register flow. The `next` param
        // carries the original destination so the register page returns
        // the candidate to `/jobs/:slug/apply` after successful signup.
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        return <Navigate to={`/candidate/register?next=${next}`} replace />;
    }
    return children;
}

function RootRedirect() {
    // Home is a public landing/jobs board page — visible to everyone (logged out, employers, candidates).
    return <HomePage />;
}

// Iter87 Audit — /jobs/:slug/screening (legacy ScreeningPage) now redirects
// to /jobs/:slug/apply so any lingering or emailed legacy link lands on the
// modular router. Query string is preserved so token/utm params carry over.
function LegacyScreeningRedirect() {
    const { slug } = useParams();
    const { search } = useLocation();
    return <Navigate to={`/jobs/${slug}/apply${search || ""}`} replace />;
}

// Billing v3 (Oct 2026) — /billing is the canonical short link; lands on the
// v3 billing page when the flag is ON, legacy /dashboard/billing otherwise.
function BillingRedirect() {
    const [target, setTarget] = useState(null);
    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing-v3/status`, { credentials: "include" })
            .then((r) => r.json())
            .then((st) => setTarget(st?.enabled ? "/dashboard/billing-v3" : "/dashboard/billing"))
            .catch(() => setTarget("/dashboard/billing"));
    }, []);
    if (!target) return <RouteFallback />;
    return <Navigate to={target} replace />;
}

// Batch A6 (2026-03-02) — Microsoft Clarity SPA glue. Mounts inside
// BrowserRouter so useLocation is available. Bootstraps the global
// mask selector list once, and re-syncs recording enable/disable on
// every route change so /bmi-ops (admin console) is excluded.
function ClarityLifecycle() {
    const location = useLocation();
    useEffect(() => { bootstrapClarityMasks(); }, []);
    useEffect(() => { syncClarityForRoute(location.pathname); },
              [location.pathname]);
    // Batch A13 (2026-03-02) — capture first-touch UTM/click-id/
    // referrer/landing snapshot into sessionStorage on the FIRST
    // render. `initUtmSession` is a no-op if a snapshot already
    // exists (first-touch semantics). Attribution then survives
    // route changes; the backend copies it onto the application
    // doc at /apply/:slug/entry submit.
    useEffect(() => { initUtmSession(); }, []);
    // Batch A15 (2026-03-02) — SPA $pageview tracking.
    //
    // Why this exists: posthog-js has two failure modes we had to
    // eliminate. (1) `capture_pageview: 'history_change'` was
    // unreliable across posthog-js versions — under-counted the
    // very first hard load in some builds, and the CDN can silently
    // change its default behaviour. (2) Our previous polling
    // workaround (20×100ms setTimeout waiting for window.posthog)
    // dropped events on slow mobile devices where the SDK hadn't
    // hydrated within 2s (LCP 8s+ on low-end Android inside the
    // Instagram in-app browser is common).
    //
    // Fix: `posthog.init({ capture_pageview: false, loaded: ph =>
    // ph.capture("$pageview") })` in index.html fires the initial
    // pageview the moment the SDK is ready — no polling, no race.
    // Then this effect fires exactly one $pageview per subsequent
    // route change, deduplicated on full path+search so back/forward
    // to the same URL doesn't double-fire and UTM changes count as
    // a distinct view (needed for attribution).
    //
    // Skip the very first render: `loaded` in index.html already
    // captured the landing pageview, so firing here on mount would
    // double-count.
    useEffect(() => {
        // Use a ref-like pattern via a module-scoped last-seen URL
        // held on window so React StrictMode double-invoke doesn't
        // double-fire in dev.
        const url = location.pathname + location.search;
        if (typeof window === "undefined") return;
        if (!window.__ph_pv_seen) {
            // First render — `loaded` callback already captured
            // this URL as the landing $pageview. Just record it
            // so route changes back to the landing URL still
            // qualify as fresh views elsewhere in the session.
            window.__ph_pv_seen = url;
            return;
        }
        if (window.__ph_pv_seen === url) return; // dedupe
        window.__ph_pv_seen = url;
        try { window.posthog?.capture?.("$pageview"); } catch { /* silent */ }
    }, [location.pathname, location.search]);
    return null;
}

function App() {
    return (
        <ErrorBoundary>
        <HelmetProvider>
            <AuthProvider>
                <CandidateAuthProvider>
                    <BrowserRouter>
                        <AnalyticsProvider>
                            <ClarityLifecycle />
                            <HelpBotProvider>
                            <BetaBanner />
                            <Suspense fallback={<RouteFallback />}>
                            <Routes>
                        {/* Root — public homepage with jobs board + search */}
                        <Route path="/" element={<RootRedirect />} />

                        {/* Public marketing / legal pages */}
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/apply/thank-you" element={<ApplyThankYouPage />} />

                        {/* Batch 1 Social Boost (2026-02-11) — paid-social
                            front-door. Behind runtime kill-switch flag
                            (see useBoostFlow.js). When OFF, this page
                            transparently redirects to /jobs/:slug/start
                            so paid links keep working unchanged. */}
                        <Route path="/go/:slug" element={<SocialBoostLandingPage />} />
                        {/* Book-First journey (2026-06) — second boost link,
                            flag-gated (social_bookfirst_journey_enabled).
                            When OFF, redirects to /jobs/:slug. */}
                        <Route path="/go/:slug/book-first" element={<BookFirstLandingPage />} />

                        {/* Employer auth */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/verify-otp" element={<VerifyOtpPage />} />
                        {/* Notice-and-object (2026-06) — public one-click RM opt-out */}
                        <Route path="/rm-optout/:token" element={<RmOptOutPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/auth/callback" element={<AuthCallbackPage />} />

                        {/* Candidate auth */}
                        <Route path="/candidate/register" element={<CandidateRegisterPage />} />
                        <Route path="/candidate/login" element={<CandidateLoginPage />} />
                        <Route path="/candidate/verify-otp" element={<CandidateVerifyOtpPage />} />
                        <Route
                            path="/candidate/dashboard"
                            element={<CandidateProtectedRoute><CandidateDashboardPage /></CandidateProtectedRoute>}
                        />
                        <Route
                            path="/candidate/profile"
                            element={<CandidateProtectedRoute><CandidateProfilePage /></CandidateProtectedRoute>}
                        />

                        {/* Employer profile completion & plans */}
                        <Route
                            path="/complete-profile"
                            element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>}
                        />
                        <Route
                            path="/plans"
                            element={<ProtectedRoute><PlansPage /></ProtectedRoute>}
                        />

                        {/* Employer protected — require plan */}
                        <Route path="/dashboard" element={<ProtectedRoute requirePlan><ErrorBoundary name="dashboard"><DashboardPage /></ErrorBoundary></ProtectedRoute>} />
                        <Route path="/dashboard/action-queue" element={<ProtectedRoute requirePlan><ErrorBoundary name="action-queue"><ActionQueueListPage /></ErrorBoundary></ProtectedRoute>} />
                        <Route path="/dashboard/new-job" element={<ProtectedRoute requirePlan><ConsultantApp /></ProtectedRoute>} />
                        {/* Batch 6 Phase A (2026-02-28) — Suggestive JD (draft-first-refine).
                            New default authoring surface for job creation. The
                            existing chat-first consultant flow stays at
                            /dashboard/new-job and is linked from here as the
                            exception path. */}
                        <Route path="/dashboard/new-job/seed" element={<ProtectedRoute requirePlan><JobSeedPage /></ProtectedRoute>} />
                        <Route path="/dashboard/chat/:sessionId" element={<ProtectedRoute requirePlan><ConsultantApp /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/slots" element={<ProtectedRoute requirePlan><SlotsPage /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/screening-preview" element={<ProtectedRoute requirePlan><ScreeningPreviewPage /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/edit" element={<ProtectedRoute requirePlan><JobEditPage /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/screening-builder" element={<ProtectedRoute requirePlan><ScreeningBuilderPage /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/pipeline" element={<ProtectedRoute requirePlan><ErrorBoundary name="pipeline"><JobPipelinePage /></ErrorBoundary></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/campaigns" element={<ProtectedRoute requirePlan><JobCampaignsPage /></ProtectedRoute>} />
                        <Route path="/dashboard/jobs/:jobId/funnel" element={<ProtectedRoute requirePlan><JobFunnelPage /></ProtectedRoute>} />
                        <Route path="/dashboard/applications/:appId" element={<ProtectedRoute requirePlan><ApplicationReportPage /></ProtectedRoute>} />
                        {/* Iteration 44 — /admin and /auditor retired; consolidated into /bmi-ops. */}
                        <Route path="/admin" element={<Navigate to="/bmi-ops" replace />} />
                        <Route path="/admin/*" element={<Navigate to="/bmi-ops" replace />} />
                        <Route path="/billing" element={<ProtectedRoute><BillingRedirect /></ProtectedRoute>} />
                        <Route path="/dashboard/billing" element={<ProtectedRoute><RequireCapability capability="billing.view" subject="billing"><BillingPage /></RequireCapability></ProtectedRoute>} />
                        <Route path="/dashboard/billing-v3" element={<ProtectedRoute><RequireCapability capability="billing.view" subject="billing"><BillingV3Page /></RequireCapability></ProtectedRoute>} />
                        <Route path="/dashboard/usage" element={<ProtectedRoute><RequireCapability capability="usage.view" subject="usage"><UsagePage /></RequireCapability></ProtectedRoute>} />
                        <Route path="/dashboard/company-profile" element={<ProtectedRoute><RequireCapability capability="company_profile.view" subject="company profile"><CompanyProfilePage /></RequireCapability></ProtectedRoute>} />
                        <Route path="/dashboard/team" element={<ProtectedRoute><RequireCapability capability="team.view" subject="team management"><TeamMembersPage /></RequireCapability></ProtectedRoute>} />
                        <Route path="/dashboard/account" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
                        <Route path="/auditor" element={<Navigate to="/bmi-ops" replace />} />
                        <Route path="/auditor/*" element={<Navigate to="/bmi-ops" replace />} />

                        {/* Public jobs portal + candidate application flow (login-gated for /apply) */}
                        <Route path="/jobs" element={<PublicJobsListPage />} />
                        <Route path="/jobs/:slug" element={<PublicJobDetailPage />} />
                        <Route path="/jobs/:slug/apply" element={<CandidateApplyProtectedRoute><ApplyPage /></CandidateApplyProtectedRoute>} />
                        {/* Batch A3 (2026-03-02) — account-first apply
                            flow. `/apply/:slug/entry` is the new
                            low-friction step 1 (email + consent → OTP);
                            `/apply/:slug/entry/:appId/verify` is step 2
                            (enter OTP). Step 3 (CV) reuses the existing
                            `/jobs/:slug/apply` page. */}
                        <Route path="/apply/:slug/entry" element={<ApplyEntryPage />} />
                        <Route path="/apply/:slug/entry/:appId/verify" element={<ApplyEntryVerifyPage />} />
                        {/* Phase 2 Cycle A (2026-02-25, R4) — public
                            pre-journey notice. Rendered BEFORE any auth
                            gate so candidates always see what the
                            journey involves before we ask for details. */}
                        <Route path="/jobs/:slug/start" element={<JourneyIntroPage />} />
                        {/* Phase 2 Cycle D (2026-02-25) — compulsory OCEAN step
                            between MCQ and AV. Public route: OceanStepPage
                            authenticates via the URL-supplied X-Candidate-Token,
                            same pattern as CvStepPage. */}
                        <Route path="/apply/ocean/:appId" element={<OceanStepPage />} />
                        {/* Cycle D — employer JD consultation: capture / edit
                            per-role OCEAN target bands. Employer-only. */}
                        <Route path="/employer/jobs/:jobId/target-ocean" element={<OceanTargetPage />} />
                        <Route path="/jobs/:slug/mcq" element={<CandidateApplyProtectedRoute><McqTakePage /></CandidateApplyProtectedRoute>} />
                        <Route path="/jobs/:slug/screening" element={<LegacyScreeningRedirect />} />
                        <Route path="/jobs/:slug/result" element={<CandidateApplyProtectedRoute><ResultPage /></CandidateApplyProtectedRoute>} />
                        {/* Phase 3b-1 — public magic link for audio/video task */}
                        <Route path="/av-task/:token" element={<AvTaskPage />} />
                        {/* Iter73 (2026-02-25) — Post-MCQ CV step */}
                        <Route path="/apply/mcq/:appId/cv" element={<CvStepPage />} />
                        {/* Phase 3b-4 — public magic link for interviewer scorecard */}
                        <Route path="/scorecard/:token" element={<ScorecardPage />} />

                        {/* Big-Five personality assessment (candidate only) */}
                        <Route path="/candidate/personality-assessment" element={<CandidateProtectedRoute><PersonalityAssessmentPage /></CandidateProtectedRoute>} />
                        <Route path="/candidate/personality-report" element={<CandidateProtectedRoute><PersonalityReportPage /></CandidateProtectedRoute>} />

                        {/* Public, signed interviewer brief (no login) */}
                        <Route path="/interviewer-brief/:token" element={<InterviewerBriefPage />} />

                        {/* Public: accept a team invite (no login required) */}
                        <Route path="/employer/accept-invite" element={<AcceptInvitePage />} />
                        {/* Batch 3 (2026-02-28) — Interviewer role landing page.
                            Auto-redirected here by ProtectedRoute when
                            team_role === "INTERVIEWER". Recruiters / Admins
                            can also visit /interviewer manually to see
                            interviews on their own plate. */}
                        <Route path="/interviewer" element={<ProtectedRoute><InterviewerHomePage /></ProtectedRoute>} />

                        {/* Internal ops (Super admin / RBAC) — self-contained auth */}
                        {/* Iter61 Phase 2 — Journey Live board. Must come BEFORE /bmi-ops/* catch-all. */}
                        <Route path="/bmi-ops/live" element={<JourneyLivePage />} />
                        <Route path="/bmi-ops/billing-v3" element={<BillingV3OpsPanel />} />
                        {/* Mobile-backfill (2026-06) — add+verify mobile deep-link targets. */}
                        <Route path="/bmi-ops/verify-mobile" element={<VerifyMobilePage audience="staff" />} />
                        <Route path="/verify-mobile" element={<VerifyMobilePage audience="employer" />} />
                        <Route path="/candidate/verify-mobile" element={<VerifyMobilePage audience="candidate" />} />
                        <Route path="/bmi-ops" element={<BmiOpsPage />} />
                        <Route path="/bmi-ops/*" element={<BmiOpsPage />} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                            </Suspense>
                            {/* Phase 3a — first-login employer agreement (Task 8) */}
                            <EmployerAgreementModal />
                            {/* Batch iOS-P2 — Help & Feedback bot; self-suppresses on
                                public / auth / AV-recording routes. */}
                            <HelpBotMount />
                            </HelpBotProvider>
                        </AnalyticsProvider>
                    </BrowserRouter>
            </CandidateAuthProvider>
        </AuthProvider>
        </HelmetProvider>
        </ErrorBoundary>
    );
}

export default App;
